// ============================================================================
// Time & Labor — timecards. Every shift segment, filterable by worker and
// facility, with the correction drawer.
//
// ⛔ R-08, Ben's rule from the meeting: THERE IS NO SAVE PATH WITHOUT A REASON.
// The save button is disabled until the reason field has content, and the RPC
// refuses without one anyway (TCS_TIME_REASON_REQUIRED) — the UI mirror is a
// courtesy, the database is the rule.
// ⛔ R-21: a shift cannot end before it starts; the drawer surfaces the
// refusal on the time fields (TCS_TIME_NEGATIVE_DURATION).
// ============================================================================

import { canReviewTime, hasFullAmAuthority, isAmRole } from '../../shared/role-gates';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, PageHeader, TabBar } from '../../shared/components';
import { useAuth } from '../../shared/auth';
import { describeError } from '../../shared/errors';
import { RemovedTable, SegmentTable } from './console-parts';
import { formatSiteDateTime, formatSiteTime, isoToSiteInput, siteInputToISO, tzLongLabel } from '../../lib/siteTime';
import { marketOptions, useMarkets } from '../../shared/markets';
import { useTimeTabsNav } from './time-console';
import { WorkerPicker } from './worker-picker';
import {
  correctSegment,
  manualSegment,
  useFacilityOptions,
  usePunchHistory,
  useRemovedSegments,
  useSegments,
  useServiceLines,
  useSiteTz,
  useWorkerPicker,
} from './hooks/use-time-data';
import { FIELD_BY_HINT, type FacilityOption, type RpcHintToken, type SegmentRow } from './types';

// ⛔ EVERY TIME ON THIS SCREEN IS ON THE WORKER'S CLOCK (Ben, 2026-09-24).
// Shown with its label, and read back from a time field as the building's time
// — never the typist's. Antonela typed 8:00 PM from Madrid for a St. Louis night
// and the form saved 1:00 PM; `siteTime` is the one way in and out.

/** Who is acting, in the terms the time saves test. */
export interface ShiftReach {
  /** HR, executive or superuser — every shift (canReviewTime). */
  isStaff: boolean;
  /** is_am_role(): account manager or national AM. */
  amSide: boolean;
  /** has_full_am_authority(): every building is theirs. */
  fullAm: boolean;
  myEid: string | null;
}

/** Mirrors rpc_time_correct_segment's check: staff, or an AM at a building that
 *  is_my_facility() admits; a shift with no building needs full AM authority. */
export function mayActOnShift(r: ShiftReach, facilityId: string | null, rows: FacilityOption[]): boolean {
  if (r.isStaff) return true;
  if (facilityId == null) return r.fullAm;
  if (!r.amSide) return false;
  return r.fullAm || rows.some((f) => f.facility_id === facilityId && r.myEid != null && f.account_manager_eid === r.myEid);
}

/** The buildings "Record a shift manually" offers — only those
 *  rpc_time_manual_segment will accept (Ben, 2026-10-26, #982). */
export function manualShiftBuildings(r: ShiftReach, rows: FacilityOption[]): FacilityOption[] {
  if (r.isStaff) return rows;
  return rows.filter((f) => mayActOnShift(r, f.facility_id, rows));
}

export function TimeTimecards() {
  const { profile } = useAuth();
  const tabsNav = useTimeTabsNav('timecards');
  const [searchParams, setSearchParams] = useSearchParams();

  const [workerId, setWorkerId] = useState('');
  const [facilityId, setFacilityId] = useState('');
  const filter = useMemo(
    () => ({ workerId: workerId || undefined, facilityId: facilityId || undefined }),
    [workerId, facilityId],
  );
  const { rows, loading, error, reload } = useSegments(filter);
  // The other half of the same record: what was removed from whoever is
  // filtered on screen. Kept out of the card, one click away from it.
  const removed = useRemovedSegments(filter);
  // Fetched per row, only when somebody opens it — 300 shifts must not
  // fetch 300 chains nobody asked for.
  const history = usePunchHistory();
  const [showRemoved, setShowRemoved] = useState(false);
  const workers = useWorkerPicker();
  const facilities = useFacilityOptions();
  const serviceLines = useServiceLines();
  // ⛔ The market box offers the real markets and nothing else. Free text is
  // how "St. louis" got onto a shift, matching no market (2026-09-24).
  const { markets } = useMarkets();
  const activeMarkets = marketOptions(markets);

  const role = profile?.role ?? 'none';
  const myEid = profile?.employee_id ?? null;
  // HR, executives and superusers act on every shift, as before.
  const isStaff = canReviewTime(role);
  // ⛔ An account manager's reach on this screen is the database's own rule,
  // the one rpc_time_correct_segment and rpc_time_manual_segment share:
  // is_am_role() AND is_my_facility(<building>). is_my_facility() is the AM's
  // own buildings, or every building for has_full_am_authority() (the national
  // AM). A shift booked to a market with no building stays with HR, except
  // that a correction admits full AM authority there too (Ben, 2026-10-26,
  // items #981 and #982). Mirrored here so the screen offers what the save takes.
  const amSide = isAmRole(role);
  const fullAm = hasFullAmAuthority(role);
  const reach = { isStaff, amSide, fullAm, myEid };
  const canCorrectRow = (s: SegmentRow): boolean => mayActOnShift(reach, s.facility_id, facilities.rows);

  // ----- manual after-the-fact entry (call-ins) -----
  // "Kiosk does not work and my phone broke, but calling from the EVS
  // director's phone" — the shift is recorded after the fact, with the reason
  // on the record. A position, never a person: HR, executives, and since
  // 2026-10-26 an account manager at a building the save accepts (#982).
  const canRecord = isStaff || amSide;
  // The building list offers only buildings the save will accept.
  const manualFacilities = useMemo(
    () => manualShiftBuildings({ isStaff, amSide, fullAm, myEid }, facilities.rows),
    [isStaff, amSide, fullAm, myEid, facilities.rows],
  );
  const [showManual, setShowManual] = useState(false);
  const [mWorker, setMWorker] = useState('');
  const [mKind, setMKind] = useState<'facility' | 'market'>('facility');
  const [mFacility, setMFacility] = useState('');
  const [mMarket, setMMarket] = useState('');
  const [mLine, setMLine] = useState('evs');
  const [mShift, setMShift] = useState('');
  const [mIn, setMIn] = useState('');
  const [mOut, setMOut] = useState('');
  const [mReason, setMReason] = useState('');
  const [mBusy, setMBusy] = useState(false);
  const [mError, setMError] = useState<string | null>(null);
  const [mFieldError, setMFieldError] = useState<'reason' | 'times' | null>(null);
  const [mStatus, setMStatus] = useState<string | null>(null);
  // The clock the typed times are on: the chosen building's market, or the
  // chosen market. The time fields stay shut until it is known (Decision 4).
  const mSite = useSiteTz(
    mKind === 'facility' ? mFacility || null : null,
    mKind === 'market' ? mMarket || null : null,
  );
  const mTz = mSite.tz;

  async function submitManual() {
    setMBusy(true);
    setMError(null);
    setMFieldError(null);
    setMStatus(null);
    try {
      const startedAt = mTz ? siteInputToISO(mIn, mTz) : null;
      const endedAt = mTz ? siteInputToISO(mOut, mTz) : null;
      if (!startedAt || !endedAt) throw new Error('Pick the building or market first, then enter both times.');
      const r = await manualSegment({
        workerId: mWorker,
        startedAt,
        endedAt,
        reason: mReason.trim(),
        facilityId: mKind === 'facility' ? mFacility : null,
        marketRegion: mKind === 'market' ? mMarket : null,
        serviceLineCode: mLine || null,
        shiftCode: mShift || null,
      });
      setMStatus(`Recorded ${r.segment_id} · ${(r.raw_minutes / 60).toFixed(2)} h. Your name is on both punches.`);
      setMIn(''); setMOut(''); setMReason('');
      await reload();
    } catch (e) {
      const d = describeError(e, 'Could not record the shift.');
      const hint = (d.hint ?? '') as RpcHintToken;
      const field = FIELD_BY_HINT[hint];
      if (field === 'reason' || field === 'times') setMFieldError(field);
      setMError(d.message);
    } finally {
      setMBusy(false);
    }
  }

  // ⛔ THE REASON IS OPTIONAL ON AN ADD, AND ON NOTHING ELSE (Ben approved
  // option A, 2026-09-22). On an add the times themselves are the evidence
  // and HR is usually keying in a call-in they just took. On an edit and on a
  // removal the sentence IS the evidence that the original was wrong, so both
  // keep the requirement — see the drawer below and rpc_time_correct_segment.
  const manualReady =
    mWorker !== '' && mIn !== '' && mOut !== '' && mTz !== null &&
    (mKind === 'facility' ? mFacility !== '' : mMarket !== '');

  // ⛔ THE WORKER ALREADY CHOSEN ON THE SCREEN CARRIES INTO THE FORM (Ben,
  // 2026-09-22: "when HR already has the worker selected and clicks 'add a
  // shift', the form should automatically pull up the worker in view"). It
  // follows the filter while nobody has touched the form's own picker, and
  // stops the moment somebody does — so a deliberate choice is never
  // overwritten by a filter change.
  const [mWorkerTouched, setMWorkerTouched] = useState(false);
  useEffect(() => {
    if (!mWorkerTouched) setMWorker(workerId);
  }, [workerId, mWorkerTouched]);

  // Correction drawer state.
  //
  // ⛔ EDIT AND REMOVE ARE TWO ACTIONS, NOT ONE WITH A CHECKBOX (Ben,
  // 2026-09-22). They still call the same function — removing a shift has
  // always been a correction that voids — but nobody looking to delete a bad
  // punch goes hunting under "correct", so the drawer opens in one mode or
  // the other and says which.
  const [target, setTarget] = useState<SegmentRow | null>(null);
  const [mode, setMode] = useState<'edit' | 'remove'>('edit');
  const [inAt, setInAt] = useState('');
  const [outAt, setOutAt] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<'reason' | 'times' | null>(null);
  const voidShift = mode === 'remove';

  function openDrawer(s: SegmentRow, how: 'edit' | 'remove' = 'edit') {
    setTarget(s);
    setMode(how);
    setInAt(isoToSiteInput(s.started_at, s.site_tz));
    setOutAt(isoToSiteInput(s.ended_at ?? s.started_at, s.site_tz));
    setReason('');
    setFormError(null);
    setFieldError(null);
  }

  // Deep link from the queue: /time/timecards?correct=SEG-0001
  const correctParam = searchParams.get('correct');
  useEffect(() => {
    if (!correctParam || rows.length === 0) return;
    const s = rows.find((r) => r.segment_id === correctParam);
    if (s && canCorrectRow(s)) {
      openDrawer(s);
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctParam, rows]);

  async function submitCorrection() {
    if (!target) return;
    setBusy(true);
    setFormError(null);
    setFieldError(null);
    try {
      await correctSegment(
        target.segment_id,
        voidShift ? null : siteInputToISO(inAt, target.site_tz),
        voidShift ? null : siteInputToISO(outAt, target.site_tz),
        reason.trim(),
        voidShift,
      );
      setTarget(null);
      // Both halves of the record move together — the shift leaves the card
      // and lands in the removed list in the same breath, so nobody sees it
      // vanish with nothing to show for it.
      await Promise.all([reload(), removed.reload()]);
      if (voidShift) setShowRemoved(true);
    } catch (e) {
      const d = describeError(e, 'Could not save the correction.');
      const hint = (d.hint ?? '') as RpcHintToken;
      const field = FIELD_BY_HINT[hint];
      if (field === 'reason' || field === 'times') setFieldError(field);
      setFormError(d.message);
    } finally {
      setBusy(false);
    }
  }

  const reasonMissing = reason.trim() === '';

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <PageHeader
        eyebrow="Time & Labor"
        title="Timecards"
        meta={loading ? 'Loading…' : `${rows.length} shifts · corrections supersede, never edit — both punches stay on the record forever`}
        actions={<Button size="sm" variant="secondary" onClick={() => void reload()}>Refresh</Button>}
      />
      <div className="mt-3"><TabBar {...tabsNav} /></div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {/* ⛔ TCSD-0171 — SmartSelect is THE picker. A flat list of 116 names
            is how "I still cannot find William" happens. */}
        <div className="min-w-[240px] flex-1 sm:max-w-xs">
          <WorkerPicker
            rows={workers.rows}
            value={workerId || null}
            onChange={setWorkerId}
            placeholder="All workers — type a name…"
            allowClear
          />
        </div>
        <select
          value={facilityId}
          onChange={(e) => setFacilityId(e.target.value)}
          className="h-9 max-w-xs rounded-lg border border-tcs-border-input bg-tcs-field px-3 text-sm text-tcs-text"
          aria-label="Filter by facility"
        >
          <option value="">All facilities</option>
          {facilities.rows.map((f) => (
            <option key={f.facility_id} value={f.facility_id}>{f.display}</option>
          ))}
        </select>
        {canRecord && (
          <button
            type="button"
            onClick={() => setShowManual((s) => !s)}
            className={`ml-auto rounded-lg px-3 py-1.5 text-xs font-semibold ${showManual
              ? 'bg-tcs-inverse text-tcs-inverse-ink'
              : 'border border-tcs-border-strong bg-tcs-bg text-tcs-text hover:bg-tcs-surface'}`}
          >
            Record a shift manually…
          </button>
        )}
      </div>

      {canRecord && showManual && (
        <div className="mt-3 rounded-xl border border-tcs-border bg-tcs-raised p-4">
          <div className="text-sm font-semibold text-tcs-text">Manual after-the-fact entry (call-in)</div>
          <div className="mt-1 text-xs text-tcs-text-secondary">
            For when the kiosk was down or the phone was broken and the worker called the times in.
            Both punches are recorded as supervisor-manual with your name on the permanent record —
            this is a real entry, not a correction of one.
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <div className="min-w-[230px]">
              <WorkerPicker
                rows={workers.rows}
                value={mWorker || null}
                onChange={(id) => { setMWorkerTouched(true); setMWorker(id); }}
                placeholder="Worker…"
              />
            </div>
            {/* A shift booked to a market with no building stays with HR and
                executives; the save refuses an account manager there. */}
            {isStaff && (
              <select value={mKind} onChange={(e) => setMKind(e.target.value as 'facility' | 'market')}
                className="h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm text-tcs-text" aria-label="Place kind">
                <option value="facility">Facility</option>
                <option value="market">Market (roving)</option>
              </select>
            )}
            {mKind === 'facility' ? (
              <select value={mFacility} onChange={(e) => setMFacility(e.target.value)}
                className="h-9 max-w-[260px] rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm text-tcs-text" aria-label="Facility for manual entry">
                <option value="">{isStaff || fullAm ? 'Facility…' : 'One of your buildings…'}</option>
                {manualFacilities.map((f) => <option key={f.facility_id} value={f.facility_id}>{f.display}</option>)}
              </select>
            ) : (
              <select value={mMarket} onChange={(e) => setMMarket(e.target.value)}
                className="h-9 max-w-[220px] rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm text-tcs-text" aria-label="Market for manual entry">
                <option value="">Market…</option>
                {activeMarkets.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            )}
            <select value={mLine} onChange={(e) => setMLine(e.target.value)}
              className="h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm text-tcs-text" aria-label="Service line for manual entry">
              {serviceLines.map((l) => <option key={l.service_line_code} value={l.service_line_code}>{l.label}</option>)}
            </select>
            <select value={mShift} onChange={(e) => setMShift(e.target.value)}
              className="h-9 rounded-lg border border-tcs-border-input bg-tcs-field px-2 text-sm text-tcs-text" aria-label="Shift for manual entry">
              <option value="">Shift…</option>
              <option value="1st">1st</option><option value="2nd">2nd</option><option value="3rd">3rd</option>
              <option value="night">Night</option><option value="weekend">Weekend</option><option value="flex">Flex</option>
            </select>
          </div>
          <div className={`mt-3 text-xs ${mTz ? 'font-semibold text-tcs-text' : 'text-tcs-text-muted'}`}>
            {mSite.error
              ? <span className="text-st-danger-fg">{mSite.error}</span>
              : mTz
                ? `Enter the time where the worker was — ${tzLongLabel(mTz)}.`
                : 'Pick the building or market first. The times are entered on that building’s clock.'}
          </div>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <div>
              <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="manual-in">Punch in (worker&rsquo;s time)</label>
              <input id="manual-in" type="datetime-local" value={mIn} onChange={(e) => setMIn(e.target.value)} disabled={!mTz}
                className={`mt-1 block rounded-lg border bg-tcs-field px-3 py-2 text-sm text-tcs-text ${mFieldError === 'times' ? 'border-st-danger-solid' : 'border-tcs-border-input'}`} />
            </div>
            <div>
              <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="manual-out">Punch out (worker&rsquo;s time, must be in the past)</label>
              <input id="manual-out" type="datetime-local" value={mOut} onChange={(e) => setMOut(e.target.value)} disabled={!mTz}
                className={`mt-1 block rounded-lg border bg-tcs-field px-3 py-2 text-sm text-tcs-text ${mFieldError === 'times' ? 'border-st-danger-solid' : 'border-tcs-border-input'}`} />
            </div>
            <div className="min-w-[260px] flex-1">
              <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="manual-reason">
                Reason <span className="text-tcs-text-muted">optional</span>
              </label>
              <input id="manual-reason" value={mReason} onChange={(e) => setMReason(e.target.value)}
                placeholder="Anything worth knowing later. Leave blank if there is nothing."
                className={`mt-1 block w-full rounded-lg border bg-tcs-field px-3 py-2 text-sm text-tcs-text ${mFieldError === 'reason' ? 'border-st-danger-solid' : 'border-tcs-border-input'}`} />
            </div>
            <Button size="sm" loading={mBusy} disabled={!manualReady} onClick={() => void submitManual()}>
              Record shift
            </Button>
          </div>
          {mError && <div className="mt-2 text-sm text-st-danger-fg">{mError}</div>}
          {mStatus && <div className="mt-2 text-sm text-st-ok-fg">{mStatus}</div>}
        </div>
      )}

      {error && (
        <div className="mt-4 rounded-xl border border-st-danger-border bg-st-danger-bg px-4 py-3 text-sm text-st-danger-fg">
          {error}
        </div>
      )}

      <div className="mt-4">
        <SegmentTable
          rows={rows}
          canCorrect={canCorrectRow}
          onCorrect={(s) => openDrawer(s, 'edit')}
          onRemove={(s) => openDrawer(s, 'remove')}
          historyFor={history.segmentId}
          onToggleHistory={(id) => void history.open(id)}
          history={history.rows}
          historyLoading={history.loading}
          historyError={history.error}
        />
      </div>

      {/* ⛔ REMOVED SHIFTS ARE OUT OF THE CARD, NOT OUT OF SIGHT (Ben,
          2026-09-22). One quiet line, one click from where somebody just
          removed something, scoped to whoever is filtered on screen. The
          whole-company view is the Removed tab, which is this same list with
          no filter. */}
      {removed.rows.length > 0 && (
        <div className="mt-3 border-t border-tcs-border pt-3">
          <button
            type="button"
            onClick={() => setShowRemoved((s) => !s)}
            className="text-xs text-tcs-text-muted underline hover:text-tcs-text"
          >
            {removed.rows.length} removed shift{removed.rows.length === 1 ? '' : 's'}
            {workerId ? ` for ${workers.rows.find((w) => w.worker_id === workerId)?.display_name ?? 'this worker'}` : ''}
            {' — '}{showRemoved ? 'hide' : 'show'}
          </button>
          {showRemoved && (
            <div className="mt-2">
              <RemovedTable rows={removed.rows} showWorker={workerId === ''} />
            </div>
          )}
        </div>
      )}

      {target && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-tcs-scrim p-4 sm:items-center">
          <div className="w-full max-w-lg rounded-2xl border border-tcs-border-strong bg-tcs-overlay shadow-xl">
            <div className={`border-b border-tcs-border px-5 py-3 font-semibold ${voidShift ? 'text-st-danger-fg' : 'text-tcs-text'}`}>
              {voidShift ? 'Remove this shift' : 'Edit shift'} — {target.worker_name} · {target.place_display}
            </div>
            <div className="flex flex-col gap-3 px-5 py-4">
              {voidShift ? (
                <>
                  <div className="text-sm text-tcs-text">
                    This shift will stop counting toward anybody&rsquo;s hours or pay. The punches stay on
                    the permanent record and the removal is logged against {target.worker_name} with your
                    name on it.
                  </div>
                  <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1 text-sm">
                    <dt className="text-tcs-text-secondary">Shift</dt>
                    <dd className="text-tcs-text">{formatSiteDateTime(target.started_at, target.site_tz)}{target.ended_at ? ` – ${formatSiteTime(target.ended_at, target.site_tz)}` : ''}</dd>
                    <dt className="text-tcs-text-secondary">Where</dt>
                    <dd className="text-tcs-text">{target.place_display}</dd>
                    <dt className="text-tcs-text-secondary">Hours removed</dt>
                    <dd className="font-semibold text-tcs-text">{((target.raw_minutes ?? 0) / 60).toFixed(2)}</dd>
                  </dl>
                </>
              ) : (
                <div className="text-xs text-tcs-text-secondary">
                  The original punches are never edited. Saving inserts superseding events carrying your
                  reason, your name, and the time — the full trail stays on the record.
                </div>
              )}
              {!voidShift && (
                <div className="text-xs font-semibold text-tcs-text">
                  Enter the time where the worker was — {tzLongLabel(target.site_tz)}.
                </div>
              )}
              {!voidShift && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="correct-in">Punch in (worker&rsquo;s time)</label>
                    <input
                      id="correct-in" type="datetime-local" value={inAt}
                      onChange={(e) => setInAt(e.target.value)}
                      className={`mt-1 w-full rounded-lg border bg-tcs-field px-3 py-2 text-sm text-tcs-text ${fieldError === 'times' ? 'border-st-danger-solid' : 'border-tcs-border-input'}`}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="correct-out">Punch out (worker&rsquo;s time)</label>
                    <input
                      id="correct-out" type="datetime-local" value={outAt}
                      onChange={(e) => setOutAt(e.target.value)}
                      className={`mt-1 w-full rounded-lg border bg-tcs-field px-3 py-2 text-sm text-tcs-text ${fieldError === 'times' ? 'border-st-danger-solid' : 'border-tcs-border-input'}`}
                    />
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-tcs-text-secondary" htmlFor="correct-reason">
                  {voidShift ? 'Why' : 'Reason'} <span className="text-st-danger-fg">* required — there is no save without it</span>
                </label>
                <textarea
                  id="correct-reason" value={reason} rows={3}
                  onChange={(e) => setReason(e.target.value)}
                  className={`mt-1 w-full rounded-lg border bg-tcs-field px-3 py-2 text-sm text-tcs-text ${fieldError === 'reason' ? 'border-st-danger-solid' : 'border-tcs-border-input'}`}
                  placeholder={voidShift
                    ? 'e.g. Duplicate — she punched twice at shift start.'
                    : 'Why is this correction right, and who confirmed it?'}
                />
                {voidShift && (
                  // ⛔ The reason stays required on a removal. On an add the
                  // times are the evidence; on a removal the sentence IS the
                  // evidence that the hours were wrong.
                  <p className="mt-1 text-[11px] text-tcs-text-muted">
                    This is what appears in the removed list and on the permanent record. Write it for
                    somebody reading it in six months.
                  </p>
                )}
              </div>
              {formError && <div className="text-sm text-st-danger-fg">{formError}</div>}
              <div className="flex justify-end gap-2">
                <Button variant="secondary" size="sm" onClick={() => setTarget(null)}>Cancel</Button>
                <Button
                  size="sm"
                  loading={busy}
                  disabled={reasonMissing}
                  onClick={() => void submitCorrection()}
                >
                  {voidShift ? 'Remove shift' : 'Save changes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
